import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowRight, KeyRound, ShieldCheck, Zap, Building2 } from "lucide-react";
import { toast } from "sonner";
import VenusLogo from "../components/VenusLogo";

export default function Login() {
  const { login, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (user) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const r = await login(email, password);
    setSubmitting(false);
    if (r.ok) {
      toast.success("Connexion réussie");
      navigate("/admin");
    } else {
      toast.error(r.error || "Identifiants invalides");
    }
  };

  return (
    <div className="min-h-screen w-full flex bg-white">
      {/* Left visual */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden bg-[var(--bg-soft)]">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{
            backgroundImage:
              "url(https://static.prod-images.emergentagent.com/jobs/919d7588-34e0-49f2-815e-9c1bc4876cad/images/3def76a81218f7972eda47c696fec4211b467c2d5c324c73111480281457a24a.png)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-white/70 via-white/40 to-[var(--bg-soft)]" />
        <div className="absolute inset-0 grid-bg opacity-30 mix-blend-multiply"/>
        <div className="relative z-10 p-12 flex flex-col justify-between w-full">
          <div className="flex items-center gap-3">
            <VenusLogo size={48} className="rounded-xl shadow-lg shadow-[#C2569B]/30" />
            <div>
              <div className="font-heading text-2xl font-extrabold tracking-tight">VÉNUS<span className="text-[var(--primary)]"> MÉDICAL</span></div>
              <div className="label-mono mt-1">Matériels & Consommables Médicaux Dentaires</div>
            </div>
          </div>

          <div className="space-y-5 max-w-md">
            <div className="label-mono">Distribution moderne</div>
            <h2 className="font-heading text-4xl xl:text-5xl font-extrabold leading-[1.05] tracking-tight">
              Pilotez votre <span className="text-[var(--primary)]">dépôt.</span><br />
              Livrez sans <span className="text-[var(--primary)]">friction.</span>
            </h2>
            <p className="text-[var(--text-base,#0a0a0a)] text-base leading-relaxed font-semibold">
              Gestion grossiste complète : commandes pharmacies, bons de livraison, factures, crédits clients, livraisons régionales — depuis votre téléphone.
            </p>

            <div className="grid grid-cols-3 gap-3 pt-2">
              {[
                { Icon: ShieldCheck, label: "Sécurisé" },
                { Icon: Zap, label: "Temps réel" },
                { Icon: Building2, label: "Multi-sites" },
              ].map(({ Icon, label }) => (
                <div key={label} className="surface p-3 flex items-center gap-2">
                  <Icon size={16} className="text-[var(--primary)]" strokeWidth={2}/>
                  <span className="text-xs font-semibold">{label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 text-xs text-[var(--text-muted)] font-data">
            <span className="dot-pulse" /> SYSTÈME OPÉRATIONNEL
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <form onSubmit={onSubmit} className="w-full max-w-md space-y-6 fade-in" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2.5 mb-4">
            <VenusLogo size={40} className="rounded-xl" />
            <div>
              <div className="font-heading text-xl font-extrabold tracking-tight">VÉNUS<span className="text-[var(--primary)]"> MÉDICAL</span></div>
              <div className="label-mono">Matériels & Consommables</div>
            </div>
          </div>

          <div>
            <div className="label-mono mb-2">Authentification</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Bon retour</h1>
            <p className="text-[var(--text-muted)] mt-2 text-sm">Connectez-vous pour accéder à votre dépôt.</p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="label-mono block mb-2">Email</label>
              <input data-testid="login-email" type="email" autoComplete="off" className="input-base" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vous@officine.sn" required />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="label-mono">Mot de passe</label>
                <Link to="/mot-de-passe-oublie" data-testid="forgot-link" className="label-mono text-[var(--primary)] hover:underline flex items-center gap-1"><KeyRound size={10}/>Oublié ?</Link>
              </div>
              <input data-testid="login-password" type="password" autoComplete="off" className="input-base" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required />
            </div>
          </div>

          <button type="submit" data-testid="login-submit" disabled={submitting} className="btn-primary w-full flex items-center justify-center gap-2">
            {submitting ? "Authentification..." : <>SE CONNECTER <ArrowRight size={16} /></>}
          </button>
        </form>
      </div>
    </div>
  );
}
